import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '@/auth/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  HelpCircle,
  Send,
  Ticket,
  Shield,
  Bot,
  User,
  Square,
  SquarePen,
  ExternalLink,
  Lightbulb,
  Bug,
} from 'lucide-react';
import api from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { cn } from '@/lib/utils';
import { formatMessageContent } from '@/lib/format-error';
import { useStreaming } from '@/lib/hooks/useStreaming';
import { useTextHighlightLearnMore } from '@/lib/hooks/useTextHighlightLearnMore';
import { LearnMorePopup } from '@/components/LearnMorePopup';
import { useSearchResults } from '../../../context/SearchResultsContext';
import { ARCHE_PATH } from '../constants';

const WEBSOCKET_ENDPOINT = import.meta.env.VITE_WEBSOCKET_ENDPOINT || '';
const MAX_CONVERSATION_MESSAGES = 20;

interface TicketSuggestion {
  subject: string;
  description: string;
  category?: string;
  priority?: string;
}

interface RecommendedArche {
  archId: string;
  slug: string | null;
  name: string;
  simpleDescription: string;
  imageRef: string | null;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  ticketSuggestion?: TicketSuggestion;
  recommendedArches?: RecommendedArche[];
}

export function SupportHomePage() {
  const { user } = useAuth();
  const isAdmin = useMemo(() => user?.roles?.includes('admin') ?? false, [user?.roles]);
  const navigate = useNavigate();
  const { setSearchResults } = useSearchResults();

  // Set sidebar to empty search mode on mount — prevents suggested arches from
  // showing. The AI will populate results when it searches.
  useEffect(() => {
    setSearchResults([], '');
  }, [setSearchResults]);

  // Ticket dialog state (for AI-suggested tickets)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ticketError, setTicketError] = useState<string | null>(null);

  // Bug report dialog state
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [bugTitle, setBugTitle] = useState('');
  const [bugDescription, setBugDescription] = useState('');
  const [bugSteps, setBugSteps] = useState('');
  const [bugSubmitting, setBugSubmitting] = useState(false);
  const [bugError, setBugError] = useState<string | null>(null);

  // Suggestion dialog state
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [suggestionTitle, setSuggestionTitle] = useState('');
  const [suggestionDescription, setSuggestionDescription] = useState('');
  const [suggestionSubmitting, setSuggestionSubmitting] = useState(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);

  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [heroInput, setHeroInput] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const chatTextareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // WebSocket streaming
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [currentAssistantMessageId, setCurrentAssistantMessageId] = useState<string | null>(null);
  const authToken = authApi.getIdToken();

  const {
    streamingText,
    isComplete: streamingComplete,
    completePayload,
    error: streamingError,
    reset: resetStreaming,
  } = useStreaming({
    requestId: currentRequestId,
    authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!currentRequestId,
  });

  const combinedStreamingText = useMemo(() => {
    return Object.values(streamingText).join('');
  }, [streamingText]);

  // Text selection "Learn more" — uses a ref to avoid stale closure
  const submitRef = useRef<(text: string) => void>(() => {});
  const handleLearnMoreQuery = useCallback((query: string) => {
    submitRef.current(query);
  }, []);
  const highlight = useTextHighlightLearnMore(
    messagesContainerRef,
    handleLearnMoreQuery,
    isStreaming,
  );

  // Send message to the new dedicated support chat endpoint
  const sendToSupportAI = useCallback(
    async (
      assistantMessageId: string,
      message: string,
      conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>,
    ) => {
      try {
        const result = await api.customerSupportChat({
          message,
          conversationHistory,
        });
        setCurrentRequestId(result.requestId);
        setCurrentAssistantMessageId(assistantMessageId);
      } catch (err) {
        console.error('Support chat failed:', err);
        const errorStr = String(err).toLowerCase();
        const isAuthError = errorStr.includes('unauthorized') || errorStr.includes('token');
        const errorMessage = isAuthError
          ? 'Your session has expired. Please log out and log back in to continue.'
          : 'Sorry, something went wrong. Please try again.';

        setChatMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? { ...msg, content: errorMessage, isStreaming: false }
              : msg,
          ),
        );
        setIsStreaming(false);
      }
    },
    [],
  );

  // Submit a chat message
  const submitChatMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isStreaming) return;

      const userMessageId = `user-${Date.now()}`;
      const assistantMessageId = `assistant-${Date.now()}`;

      const userMessage: ChatMessage = { id: userMessageId, role: 'user', content: text.trim() };
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        isStreaming: true,
      };

      setChatMessages((prev) => [...prev, userMessage, assistantMessage]);
      setChatInput('');
      setIsStreaming(true);

      // Build conversation history from existing messages (exclude empty/streaming)
      const allMessages = [...chatMessages, userMessage].filter(
        (msg) => msg.content.trim().length > 0,
      );
      const truncatedMessages = allMessages.slice(-MAX_CONVERSATION_MESSAGES);
      const conversationHistory = truncatedMessages
        .filter((m) => m.role === 'user' || (m.role === 'assistant' && !m.isStreaming))
        .map((msg) => ({
          role: msg.role as 'user' | 'assistant',
          content: msg.content,
        }));

      await sendToSupportAI(assistantMessageId, text.trim(), conversationHistory);
    },
    [isStreaming, chatMessages, sendToSupportAI],
  );

  // Keep the learn-more ref in sync with submitChatMessage
  submitRef.current = submitChatMessage;

  // Update assistant message with streaming text
  useEffect(() => {
    if (currentAssistantMessageId && combinedStreamingText) {
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId ? { ...msg, content: combinedStreamingText } : msg,
        ),
      );
    }
  }, [currentAssistantMessageId, combinedStreamingText]);

  // Handle streaming completion — extract structured payload
  useEffect(() => {
    if (streamingComplete && currentAssistantMessageId) {
      setChatMessages((prev) =>
        prev.map((msg) => {
          if (msg.id !== currentAssistantMessageId) return msg;

          const updated: ChatMessage = { ...msg, isStreaming: false };

          // Extract ticket suggestion from complete payload
          if (completePayload?.ticketSuggestion) {
            updated.ticketSuggestion = completePayload.ticketSuggestion as TicketSuggestion;
          }

          // Extract arche recommendations from complete payload
          if (completePayload?.recommendedArches) {
            updated.recommendedArches = completePayload.recommendedArches as RecommendedArche[];
          }

          return updated;
        }),
      );

      // Push search results to sidebar
      if (completePayload?.searchResults) {
        const results = completePayload.searchResults as Array<{
          archId: string;
          name: string;
          simpleDescription: string;
          imageRef: string | null;
          fitnessScore: number;
        }>;
        if (results.length > 0) {
          setSearchResults(
            results.map((r) => ({
              archId: r.archId,
              slug: null,
              name: r.name,
              simpleDescription: r.simpleDescription,
              imageRef: r.imageRef,
              categoryId: null,
              categoryName: null,
              categorySlug: null,
              tags: [],
              fitnessScore: r.fitnessScore,
              invocationCount: 0,
              pricing: { model: 'free' as const, currency: 'usd' as const },
              actionPricing: null,
              hasPerUseCredits: false,
              isStatic: false,
              archeType: 'tool' as const,
              renderMode: 'default' as const,
              contributorId: '',
              isPublic: true,
              isFullScreen: false,
            })),
            'support-search',
          );
        }
      }

      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      resetStreaming();
    }
  }, [
    streamingComplete,
    currentAssistantMessageId,
    resetStreaming,
    completePayload,
    setSearchResults,
  ]);

  // Handle streaming errors
  useEffect(() => {
    if (streamingError && currentAssistantMessageId) {
      const isAuthError =
        streamingError.message.toLowerCase().includes('unauthorized') ||
        streamingError.code === 'UNAUTHORIZED';
      const errorMessage = isAuthError
        ? 'Your session has expired. Please log out and log back in to continue.'
        : `Error: ${streamingError.message}`;

      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId
            ? { ...msg, content: errorMessage, isStreaming: false }
            : msg,
        ),
      );
      setIsStreaming(false);
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      resetStreaming();
    }
  }, [streamingError, currentAssistantMessageId, resetStreaming]);

  // Auto-scroll on message changes
  useEffect(() => {
    if (chatOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, chatOpen]);

  // Auto-resize chat textarea
  useEffect(() => {
    const textarea = chatTextareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [chatInput]);

  // Focus textarea when chat is open and not streaming
  useEffect(() => {
    if (chatOpen && !isStreaming) {
      chatTextareaRef.current?.focus();
    }
  }, [chatOpen, isStreaming]);

  // Hero input submit — opens chat and sends message
  const handleHeroSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!heroInput.trim()) return;
    const query = heroInput.trim();
    setHeroInput('');
    setChatOpen(true);
    setTimeout(() => submitChatMessage(query), 100);
  };

  // Chat input submit
  const handleChatSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    submitChatMessage(chatInput);
  };

  const handleChatKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSubmit();
    }
  };

  const handleStop = useCallback(() => {
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    resetStreaming();
    setIsStreaming(false);
    setChatMessages((prev) =>
      prev.map((msg) => (msg.isStreaming ? { ...msg, isStreaming: false } : msg)),
    );
  }, [resetStreaming]);

  const handleNewChat = useCallback(() => {
    setChatMessages([]);
    setChatInput('');
    setHeroInput('');
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    resetStreaming();
    setIsStreaming(false);
    setChatOpen(false);
    setSearchResults([], '');
  }, [resetStreaming, setSearchResults]);

  // Open ticket dialog pre-filled from AI suggestion
  const handleOpenTicketFromSuggestion = (suggestion: TicketSuggestion) => {
    setSubject(suggestion.subject);
    setDescription(suggestion.description);
    setDialogOpen(true);
  };

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) return;

    setSubmitting(true);
    setTicketError(null);
    try {
      const ticket = await api.createSupportTicket({
        subject: subject.trim(),
        description: description.trim(),
      });
      setSubject('');
      setDescription('');
      setDialogOpen(false);
      toast({ title: 'Ticket created' });
      navigate(`${ARCHE_PATH}/tickets/${ticket.ticketId}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create ticket';
      setTicketError(message);
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {chatOpen ? (
        <>
          {/* Chat header bar */}
          <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
            <div className="px-4 py-2.5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">AI Support Assistant</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNewChat}
                  disabled={isStreaming}
                  className="gap-1.5"
                >
                  <SquarePen className="h-3.5 w-3.5" />
                  New Chat
                </Button>
              </div>
            </div>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto px-4 py-6">
              <div ref={messagesContainerRef} className="space-y-4">
                {chatMessages.map((message) => {
                  const isUser = message.role === 'user';
                  return (
                    <div key={message.id}>
                      <div className={cn('flex gap-2.5', isUser ? 'justify-end' : 'justify-start')}>
                        {!isUser && (
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                            <Bot className="h-3.5 w-3.5 text-primary" />
                          </div>
                        )}
                        <div
                          className={cn(
                            'max-w-[80%] rounded-xl px-3 py-2 text-sm',
                            isUser ? 'bg-primary text-primary-foreground' : 'bg-muted',
                          )}
                        >
                          <div className="break-words">
                            {message.isStreaming && !message.content ? (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <div className="flex gap-0.5">
                                  <span
                                    className="w-1.5 h-1.5 bg-current rounded-full animate-bounce-big"
                                    style={{ animationDelay: '0ms' }}
                                  />
                                  <span
                                    className="w-1.5 h-1.5 bg-current rounded-full animate-bounce-big"
                                    style={{ animationDelay: '150ms' }}
                                  />
                                  <span
                                    className="w-1.5 h-1.5 bg-current rounded-full animate-bounce-big"
                                    style={{ animationDelay: '300ms' }}
                                  />
                                </div>
                                <span className="text-xs">Thinking...</span>
                              </div>
                            ) : !isUser ? (
                              (() => {
                                const { content: displayContent, isError } = formatMessageContent(
                                  message.content,
                                );
                                return (
                                  <div
                                    className={cn(
                                      'prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ul:pl-4 prose-ol:my-1 prose-ol:pl-4 prose-li:my-0 prose-li:pl-0 prose-headings:my-1.5 prose-strong:text-foreground [&_ul_ul]:pl-3 [&_ol_ol]:pl-3',
                                      isError && 'text-amber-600 dark:text-amber-400',
                                    )}
                                  >
                                    {isError ? (
                                      displayContent
                                    ) : (
                                      <ReactMarkdown>{displayContent}</ReactMarkdown>
                                    )}
                                    {message.isStreaming && message.content && (
                                      <span className="inline-block w-1.5 h-3.5 bg-current ml-0.5 animate-pulse" />
                                    )}
                                  </div>
                                );
                              })()
                            ) : (
                              message.content
                            )}
                          </div>
                        </div>
                        {isUser && (
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                            <User className="h-3.5 w-3.5 text-primary-foreground" />
                          </div>
                        )}
                      </div>

                      {/* Ticket suggestion card */}
                      {!isUser && message.ticketSuggestion && (
                        <div className="ml-9 mt-2">
                          <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
                            <CardContent className="p-3">
                              <div className="flex items-start justify-between gap-3">
                                <div className="space-y-1 flex-1 min-w-0">
                                  <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                                    Suggested Support Ticket
                                  </p>
                                  <p className="text-sm font-medium truncate">
                                    {message.ticketSuggestion.subject}
                                  </p>
                                  <p className="text-xs text-muted-foreground line-clamp-2">
                                    {message.ticketSuggestion.description}
                                  </p>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    handleOpenTicketFromSuggestion(message.ticketSuggestion!)
                                  }
                                  className="flex-shrink-0"
                                >
                                  <Ticket className="h-3.5 w-3.5 mr-1.5" />
                                  Review & Submit
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      )}

                      {/* Arche suggestion cards */}
                      {!isUser &&
                        message.recommendedArches &&
                        message.recommendedArches.length > 0 && (
                          <div className="ml-9 mt-2">
                            {message.recommendedArches.map((arche) => (
                              <Card
                                key={arche.archId}
                                className="hover:bg-muted/50 transition-colors cursor-pointer max-w-md"
                                onClick={() => navigate(`/a/${arche.slug || arche.archId}`)}
                              >
                                <CardContent className="p-3 flex items-center gap-3">
                                  {arche.imageRef ? (
                                    <img
                                      src={arche.imageRef}
                                      alt=""
                                      className="h-8 w-8 rounded flex-shrink-0"
                                    />
                                  ) : (
                                    <div className="h-8 w-8 rounded bg-muted flex-shrink-0" />
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium">{arche.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {arche.simpleDescription}
                                    </p>
                                  </div>
                                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        )}
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </div>
          </div>

          {/* Learn more popup (portal-rendered) */}
          {highlight.isVisible && highlight.position && (
            <LearnMorePopup position={highlight.position} onClick={highlight.handleLearnMore} />
          )}

          {/* Bottom input bar */}
          <div className="flex-shrink-0 border-t border-border bg-background">
            <div className="max-w-3xl mx-auto px-4 py-4">
              <form onSubmit={handleChatSubmit} className="relative">
                <Textarea
                  ref={chatTextareaRef}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={handleChatKeyDown}
                  placeholder="Ask a follow-up question..."
                  disabled={isStreaming}
                  className="min-h-[44px] max-h-[200px] pr-14 resize-none rounded-2xl bg-muted/50 overflow-y-auto"
                  rows={1}
                  style={{ height: 'auto' }}
                />
                {isStreaming ? (
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    onClick={handleStop}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
                  >
                    <Square className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    size="icon"
                    disabled={!chatInput.trim()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-xl"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                )}
              </form>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Press Enter to send, Shift+Enter for new line
              </p>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Empty state: centered front page */}
          <div className="flex-1 flex flex-col">
            <div className="flex-1 flex flex-col justify-center">
              <div className="text-center mb-6">
                <h1 className="text-3xl font-bold mb-2">How can we help?</h1>
                <p className="text-muted-foreground max-w-lg mx-auto">
                  Chat with our AI assistant to get instant help. If you need further assistance,
                  you can create a support ticket from the chat.
                </p>
              </div>

              {/* Actions Row */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <Link to={`${ARCHE_PATH}/faq`}>
                  <Button variant="outline">
                    <HelpCircle className="h-4 w-4 mr-2" />
                    Browse FAQ
                  </Button>
                </Link>
                <Button variant="outline" onClick={() => setSuggestionOpen(true)}>
                  <Lightbulb className="h-4 w-4 mr-2" />
                  Make a Suggestion
                </Button>
                <Button variant="outline" onClick={() => setBugReportOpen(true)}>
                  <Bug className="h-4 w-4 mr-2" />
                  Report a Bug
                </Button>
                <Link to={`${ARCHE_PATH}/tickets`}>
                  <Button variant="outline">
                    <Ticket className="h-4 w-4 mr-2" />
                    My Tickets
                  </Button>
                </Link>
                {isAdmin && (
                  <Link to={`${ARCHE_PATH}/admin`}>
                    <Button variant="outline">
                      <Shield className="h-4 w-4 mr-2" />
                      Admin Dashboard
                    </Button>
                  </Link>
                )}
              </div>

              {/* Input */}
              <div className="max-w-3xl mx-auto px-4 w-full">
                <form onSubmit={handleHeroSubmit} className="relative">
                  <Textarea
                    placeholder="Ask a question..."
                    value={heroInput}
                    onChange={(e) => setHeroInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleHeroSubmit(e);
                      }
                    }}
                    className="min-h-[100px] max-h-[200px] pr-14 resize-none rounded-2xl bg-muted/50 overflow-y-auto text-base"
                    rows={3}
                  />
                  <Button
                    type="submit"
                    size="icon"
                    disabled={!heroInput.trim()}
                    className="absolute right-2 bottom-3 h-10 w-10 rounded-xl"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Press Enter to send, Shift+Enter for new line
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Ticket creation dialog — pre-filled from AI suggestion */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <form onSubmit={handleCreateTicket}>
            <DialogHeader>
              <DialogTitle>Create Support Ticket</DialogTitle>
              <DialogDescription>
                Review the details below and submit to create a support ticket.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  placeholder="Brief description of your issue"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Provide details about your issue..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  required
                />
              </div>
            </div>
            {ticketError && <p className="text-sm text-destructive">{ticketError}</p>}
            <DialogFooter>
              <Button type="submit" disabled={submitting || !subject.trim() || !description.trim()}>
                {submitting ? 'Creating...' : 'Create Ticket'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Suggestion dialog */}
      <Dialog open={suggestionOpen} onOpenChange={setSuggestionOpen}>
        <DialogContent>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!suggestionTitle.trim() || !suggestionDescription.trim()) return;
              setSuggestionSubmitting(true);
              setSuggestionError(null);
              try {
                await api.createSuggestion({
                  title: suggestionTitle.trim(),
                  description: suggestionDescription.trim(),
                });
                setSuggestionTitle('');
                setSuggestionDescription('');
                setSuggestionOpen(false);
                toast({
                  title: 'Suggestion submitted',
                  description: 'Thank you for your feedback!',
                });
              } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to submit suggestion';
                setSuggestionError(message);
              } finally {
                setSuggestionSubmitting(false);
              }
            }}
          >
            <DialogHeader>
              <DialogTitle>Make a Suggestion</DialogTitle>
              <DialogDescription>
                Share your ideas for improving Fias. We review every suggestion.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="suggestion-title">Title</Label>
                <Input
                  id="suggestion-title"
                  placeholder="Brief summary of your idea"
                  value={suggestionTitle}
                  onChange={(e) => setSuggestionTitle(e.target.value)}
                  maxLength={200}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="suggestion-description">Description</Label>
                <Textarea
                  id="suggestion-description"
                  placeholder="Describe your idea in detail — what would it do, and why would it be useful?"
                  value={suggestionDescription}
                  onChange={(e) => setSuggestionDescription(e.target.value)}
                  rows={4}
                  required
                />
              </div>
            </div>
            {suggestionError && <p className="text-sm text-destructive">{suggestionError}</p>}
            <DialogFooter>
              <Button
                type="submit"
                disabled={
                  suggestionSubmitting || !suggestionTitle.trim() || !suggestionDescription.trim()
                }
              >
                {suggestionSubmitting ? 'Submitting...' : 'Submit Suggestion'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bug report dialog */}
      <Dialog open={bugReportOpen} onOpenChange={setBugReportOpen}>
        <DialogContent>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!bugTitle.trim() || !bugDescription.trim()) return;
              setBugSubmitting(true);
              setBugError(null);
              try {
                await api.createBugReport({
                  title: bugTitle.trim(),
                  description: bugDescription.trim(),
                  stepsToReproduce: bugSteps.trim() || undefined,
                });
                setBugTitle('');
                setBugDescription('');
                setBugSteps('');
                setBugReportOpen(false);
                toast({
                  title: 'Bug report submitted',
                  description: 'Thank you for reporting this issue!',
                });
              } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to submit bug report';
                setBugError(message);
              } finally {
                setBugSubmitting(false);
              }
            }}
          >
            <DialogHeader>
              <DialogTitle>Report a Bug</DialogTitle>
              <DialogDescription>
                Help us improve Fias by reporting issues you encounter.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="bug-title">Title</Label>
                <Input
                  id="bug-title"
                  placeholder="Brief summary of the bug"
                  value={bugTitle}
                  onChange={(e) => setBugTitle(e.target.value)}
                  maxLength={200}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bug-description">What happened?</Label>
                <Textarea
                  id="bug-description"
                  placeholder="Describe what went wrong and what you expected to happen..."
                  value={bugDescription}
                  onChange={(e) => setBugDescription(e.target.value)}
                  rows={3}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bug-steps">Steps to reproduce (optional)</Label>
                <Textarea
                  id="bug-steps"
                  placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                  value={bugSteps}
                  onChange={(e) => setBugSteps(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            {bugError && <p className="text-sm text-destructive">{bugError}</p>}
            <DialogFooter>
              <Button
                type="submit"
                disabled={bugSubmitting || !bugTitle.trim() || !bugDescription.trim()}
              >
                {bugSubmitting ? 'Submitting...' : 'Submit Bug Report'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
